import { useCallback } from 'react';
import { useStore } from '@/providers/StoreProvider';
import { useAuthModal } from '@/hooks/useAuthModal';

export function useWithAuth() {
  const isLoggedIn = useStore(s => s.webAuth.isLoggedIn);
  const { openAuthFlow } = useAuthModal();

  const withAuth = useCallback(
    async <T>(
      action: () => Promise<T>,
      authConfig?: { title?: string; description?: string },
    ): Promise<T> => {
      // Check auth state BEFORE making the API call (fast UX!)
      if (!isLoggedIn) {
        await openAuthFlow(
          authConfig || {
            title: 'Log in required',
            description: 'Please log in to continue.',
          },
        );
      }

      try {
        return await action();
      } catch (err: any) {
        // Handle session expiration (401/403 with auth message)
        const isAuthError =
          err?.status === 401 ||
          (err?.status === 403 &&
            err?.message?.toLowerCase().includes('authentication credentials'));

        if (isAuthError) {
          // Retry with fresh auth
          await openAuthFlow({
            title: 'Session expired',
            description: 'Please log in again to continue.',
            ...authConfig, // Allow custom override
          });
          return await action(); // Retry the original action
        }
        throw err;
      }
    },
    [isLoggedIn, openAuthFlow],
  );

  return withAuth;
}
